/**
 * Anthropic Provider Implementation
 * Integrates with Anthropic's Claude API using Vercel AI SDK
 */

import { createAnthropic } from '@ai-sdk/anthropic';
import { generateText, generateObject, streamText } from 'ai';
import { z } from 'zod';
import {
  AIProvider,
  AIModel,
  GenerateTextParams,
  GenerateObjectParams,
  StreamTextParams,
  TextResponse,
  ObjectResponse,
  TextChunk,
  Message,
  ChatOptions,
  ChatResponse,
  ToolRequest,
  ToolResponse,
  AIError,
  AIErrorType,
} from '../types';
import { configManager } from '../config';

/**
 * Anthropic model definitions
 */
const ANTHROPIC_MODELS: AIModel[] = [
  {
    id: 'claude-3-5-sonnet-20241022',
    name: 'Claude 3.5 Sonnet',
    provider: 'anthropic',
    capabilities: {
      chat: true,
      completion: true,
      tools: true,
      vision: true,
      streaming: true,
      maxTokens: 8192,
      contextWindow: 200000,
    },
    costPer1kTokens: {
      input: 0.003,
      output: 0.015,
    },
  },
  {
    id: 'claude-3-5-haiku-20241022',
    name: 'Claude 3.5 Haiku',
    provider: 'anthropic',
    capabilities: {
      chat: true,
      completion: true,
      tools: true,
      vision: true,
      streaming: true,
      maxTokens: 8192,
      contextWindow: 200000,
    },
    costPer1kTokens: {
      input: 0.001,
      output: 0.005,
    },
  },
  {
    id: 'claude-3-opus-20240229',
    name: 'Claude 3 Opus',
    provider: 'anthropic',
    capabilities: {
      chat: true,
      completion: true,
      tools: true,
      vision: true,
      streaming: true,
      maxTokens: 4096,
      contextWindow: 200000,
    },
    costPer1kTokens: {
      input: 0.015,
      output: 0.075,
    },
  },
];

export class AnthropicProvider implements AIProvider {
  readonly id = 'anthropic';
  readonly name = 'Anthropic';
  readonly models = ANTHROPIC_MODELS;

  private getApiKey(): string {
    const config = configManager.getProviderConfig('anthropic');
    if (!config?.apiKey) {
      throw new AIError(
        AIErrorType.API_KEY_INVALID,
        'Anthropic API key not configured',
        this.id
      );
    }
    return config.apiKey;
  }

  private getModel(modelId?: string): string {
    const config = configManager.getProviderConfig('anthropic');
    return modelId || config?.defaultModel || 'claude-3-5-sonnet-20241022';
  }

  private convertMessages(messages: Message[]): any[] {
    // Anthropic has specific requirements for message formatting
    const converted: any[] = [];
    
    for (const msg of messages) {
      if (msg.role === 'system') {
        // System messages are handled separately in Anthropic
        continue;
      }
      
      converted.push({
        role: msg.role === 'assistant' ? 'assistant' : 'user',
        content: msg.content,
        ...(msg.tool_calls && { tool_calls: msg.tool_calls }),
      });
    }
    
    return converted;
  }

  private getSystemPrompt(messages: Message[], additionalSystem?: string): string | undefined {
    const systemMessages = messages.filter(m => m.role === 'system');
    const systemContent = systemMessages.map(m => m.content).join('\n\n');
    
    if (additionalSystem) {
      return systemContent ? `${systemContent}\n\n${additionalSystem}` : additionalSystem;
    }
    
    return systemContent || undefined;
  }

  async generateText(params: GenerateTextParams): Promise<TextResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(params.model);

      const anthropicProvider = createAnthropic({
        apiKey: apiKey,
      });
      
      const result = await generateText({
        model: anthropicProvider(modelId),
        prompt: params.prompt,
        ...(params.systemPrompt && { system: params.systemPrompt }),
        ...(params.temperature !== undefined && { temperature: params.temperature }),
        ...(params.maxTokens && { maxTokens: params.maxTokens }),
        ...(params.stopSequences && { stopSequences: params.stopSequences }),
      });

      return {
        text: result.text,
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
        finishReason: result.finishReason as any,
      };
    } catch (error) {
      throw this.handleError(error, params.model);
    }
  }

  async generateObject<T>(params: GenerateObjectParams<T>): Promise<ObjectResponse<T>> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(params.model);

      // Convert JSON Schema to Zod schema (simplified - you may need a proper converter)
      const schema = z.object(params.schema as any);

      const anthropicProvider = createAnthropic({
        apiKey: apiKey,
      });
      
      const result = await generateObject({
        model: anthropicProvider(modelId),
        prompt: params.prompt,
        schema,
        ...(params.systemPrompt && { system: params.systemPrompt }),
        ...(params.temperature !== undefined && { temperature: params.temperature }),
        ...(params.maxTokens && { maxTokens: params.maxTokens }),
      });

      return {
        object: result.object as T,
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
      };
    } catch (error) {
      throw this.handleError(error, params.model);
    }
  }

  async *streamText(params: StreamTextParams): AsyncIterableIterator<TextChunk> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(params.model);

      const anthropicProvider = createAnthropic({
        apiKey: apiKey,
      });
      
      const result = await streamText({
        model: anthropicProvider(modelId),
        prompt: params.prompt,
        ...(params.systemPrompt && { system: params.systemPrompt }),
        ...(params.temperature !== undefined && { temperature: params.temperature }),
        ...(params.maxTokens && { maxTokens: params.maxTokens }),
        ...(params.stopSequences && { stopSequences: params.stopSequences }),
      });

      let isFirst = true;
      for await (const chunk of result.textStream) {
        const textChunk: TextChunk = {
          text: chunk,
          isFirst,
          isLast: false,
        };
        isFirst = false;
        
        if (params.onChunk) {
          params.onChunk(textChunk);
        }
        
        yield textChunk;
      }

      // Final chunk
      yield { text: '', isFirst: false, isLast: true };
    } catch (error) {
      throw this.handleError(error, params.model);
    }
  }

  async chat(messages: Message[], options?: ChatOptions): Promise<ChatResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(options?.model);
      const convertedMessages = this.convertMessages(messages);
      const systemPrompt = this.getSystemPrompt(messages, options?.systemPrompt);

      const anthropicProvider = createAnthropic({
        apiKey: apiKey,
      });
      
      const result = await generateText({
        model: anthropicProvider(modelId),
        messages: convertedMessages,
        ...(systemPrompt && { system: systemPrompt }),
        ...(options?.temperature !== undefined && { temperature: options.temperature }),
        ...(options?.maxTokens && { maxTokens: options.maxTokens }),
        // TODO: Fix tool support for AI SDK v4
        // ...(options?.tools && { tools: options.tools }),
        // ...(options?.toolChoice && { toolChoice: options.toolChoice }),
      });

      return {
        message: {
          role: 'assistant',
          content: result.text,
          tool_calls: result.toolCalls as any,
        },
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
        finishReason: result.finishReason as any,
      };
    } catch (error) {
      throw this.handleError(error, options?.model);
    }
  }

  async *streamChat(messages: Message[], options?: ChatOptions): AsyncIterableIterator<ChatResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(options?.model);
      const convertedMessages = this.convertMessages(messages);
      const systemPrompt = this.getSystemPrompt(messages, options?.systemPrompt);

      const anthropicProvider = createAnthropic({
        apiKey: apiKey,
      });
      
      const result = await streamText({
        model: anthropicProvider(modelId),
        messages: convertedMessages,
        ...(systemPrompt && { system: systemPrompt }),
        ...(options?.temperature !== undefined && { temperature: options.temperature }),
        ...(options?.maxTokens && { maxTokens: options.maxTokens }),
        // TODO: Fix tool support for AI SDK v4
        // ...(options?.tools && { tools: options.tools }),
        // ...(options?.toolChoice && { toolChoice: options.toolChoice }),
      });

      let accumulatedText = '';
      for await (const chunk of result.textStream) {
        accumulatedText += chunk;
        yield {
          message: {
            role: 'assistant',
            content: accumulatedText,
          },
          model: modelId,
        };
      }
    } catch (error) {
      throw this.handleError(error, options?.model);
    }
  }

  async executeTools(request: ToolRequest): Promise<ToolResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(request.model);
      const convertedMessages = this.convertMessages(request.messages);
      const systemPrompt = this.getSystemPrompt(request.messages);

      const anthropicProvider = createAnthropic({
        apiKey: apiKey,
      });
      
      const result = await generateText({
        model: anthropicProvider(modelId),
        messages: convertedMessages,
        ...(systemPrompt && { system: systemPrompt }),
        // TODO: Fix tool support for AI SDK v4
        // tools: request.tools,
        // toolChoice: request.toolChoice || 'auto',
      });

      return {
        toolCalls: result.toolCalls as any || [],
        message: {
          role: 'assistant',
          content: result.text,
          tool_calls: result.toolCalls as any,
        },
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
      };
    } catch (error) {
      throw this.handleError(error, request.model);
    }
  }

  async validateApiKey(): Promise<boolean> {
    try {
      const apiKey = this.getApiKey();
      
      // Make a minimal API call to validate the key
      const anthropicProvider = createAnthropic({
        apiKey: apiKey,
      });
      
      await generateText({
        model: anthropicProvider('claude-3-5-haiku-20241022'),
        prompt: 'Hi',
        maxTokens: 1,
      });
      
      return true;
    } catch (error) {
      return false;
    }
  }

  getModelInfo(modelId: string): AIModel | undefined {
    return this.models.find(m => m.id === modelId);
  }

  listModels(): AIModel[] {
    return [...this.models];
  }

  private handleError(error: any, modelId?: string): AIError {
    const errorMessage = error.message || 'Unknown error';
    
    if (errorMessage.includes('API key') || errorMessage.includes('authentication')) {
      return new AIError(
        AIErrorType.API_KEY_INVALID,
        'Invalid Anthropic API key',
        this.id,
        modelId,
        error
      );
    }
    
    if (errorMessage.includes('rate limit')) {
      return new AIError(
        AIErrorType.RATE_LIMIT_EXCEEDED,
        'Anthropic rate limit exceeded',
        this.id,
        modelId,
        error
      );
    }
    
    if (errorMessage.includes('context length') || errorMessage.includes('maximum')) {
      return new AIError(
        AIErrorType.CONTEXT_LENGTH_EXCEEDED,
        'Context length exceeded',
        this.id,
        modelId,
        error
      );
    }
    
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return new AIError(
        AIErrorType.NETWORK_ERROR,
        'Network error connecting to Anthropic',
        this.id,
        modelId,
        error
      );
    }
    
    return new AIError(
      AIErrorType.INVALID_RESPONSE,
      `Anthropic error: ${errorMessage}`,
      this.id,
      modelId,
      error
    );
  }
}